import React, { lazy } from 'react';

import axios from 'axios';
import classNames from 'classnames';
import { clone, forEach, get, isNull, isString, trim } from 'lodash';
import { Quill } from 'react-quill';
import urlRegex from 'url-regex';

import { getTenant } from 'admin/tenants/Tenants';
import { getUser, PreferenceKey, setUserPreference } from 'admin/users/Users';
import { EmojisShorthand, getUnicodeString } from 'common/emojis/Emoji';
import { PendoEvents } from 'common/hooks/usePendoEventTracker';
import { EmojiBlot } from 'common/response-editor/blots/EmojiBlot';
import { HashTagBlot } from 'common/response-editor/blots/HashTagBlot';
import { MentionBlot } from 'common/response-editor/blots/MentionBlot';
import { TemplateUrlBlot } from 'common/response-editor/blots/TemplateUrlBlot';
import { VariableBlot } from 'common/response-editor/blots/VariableBlot';
import { convertQuillToMetadata, MetaData, MetaDataTag } from 'common/response-editor/MetadataParser';
import { defaultSpecialButtonsOptions, RichTextOptions } from 'common/response-editor/tool-bar/ToolBar';
import TrackedReactQuill from 'common/response-editor/tracked-react-quill/TrackedReactQuill';
import { VoBScoring } from 'common/response-editor/vob/VobScoring';
import { OverlayDisplay } from 'core/overlay/OverlayDisplay';
import { KeyCode } from 'core/util/KeyCode';
import { LocaleCode } from 'core/util/Locale';
import { Color } from 'core/util/Variables';

import { DisclaimerModal } from '../disclaimer-modal/DisclaimerModal';
import { ErrorMessage } from '../error-message/ErrorMessage';

import 'react-quill/dist/quill.snow.css';
import 'common/response-editor/ResponseEditor.scss';
import 'common/response-editor/vob/VobScoring.scss';

const ReactQuill = lazy(() => import('react-quill'));

// sanitize Link urls
const Link = Quill.import('formats/link');

Link.sanitize = (url: string): string => {
    let newUrl = url;

    if (!newUrl.match(/^http/)) {
        newUrl = `http://${url}`;
    }
    return newUrl;
};

Quill.register({ 'formats/variable': VariableBlot }, true);
Quill.register({ 'formats/mention': MentionBlot }, true);
Quill.register({ 'formats/emoji': EmojiBlot }, true);
Quill.register({ 'formats/template-url': TemplateUrlBlot }, true);
Quill.register({ 'formats/hashtag': HashTagBlot }, true);

interface State {
    searchStartIndex: number | null;
    searchString: string;
    templateUrl: object;
    showError: boolean;
    showAiDisclaimer: boolean;
    emptyResponse: boolean;
}

interface Props {
    toolBarId: string;
    placeholder: string;
    defaultValue?: MetaData[];
    enableRespondOnEnterKey: boolean;
    focus: boolean;
    canEdit: boolean;
    backgroundColor: string;
    enableTemplate: boolean;
    enableVariable: boolean;
    enableMention: boolean;
    enableHashTag: boolean;
    includeMentionPrefix?: boolean;
    includeHashTagPrefix?: boolean;
    richTextOptions: RichTextOptions;
    onMentionClick?: any;
    onHashTagClick?: any;
    enableEmoji: boolean;
    onTextChange: any;
    onRespond: any;
    onKeyNavigation: any;
    onCustomButtonClick: any;
    onFocus: any;
    onBlur: any;
    onCharCount: any;
    onIsTextAnalyzedChanged?: (isTextAnalyzed: boolean) => void;
    onMeetsThresholdChanged?: (meetsThreshold?: boolean) => void;
    onWidgetTrigger: any;
    twitterSelected?: boolean;
    vobReviewResponseEnabled?: boolean;
    generateAIResponseOnLoad?: boolean;
    onPaste: any;
    onManualTyping: any;
    tenantId?: number;
    reviewId?: string;
    locationId?: number;
    userId?: number;
    userLocale?: LocaleCode;
    agencyId?: number;
    loading?: boolean;
    setLoading?: (loading: boolean) => void;
    reviewCreatedAt?: string;
}

interface textHistory {
    text: string;
    meetsThreshold: boolean;
    aiGenerated: boolean;
}

export default class Editor extends React.Component<Props, State> {
    responseModules: any;

    reactQuillRef: any;

    quillRef: any;

    unprivilegedEditor: any;

    isComponentMounted = false;

    textHistory: textHistory[] = [];

    errorCount = 0;

    isTextAnalyzed = false;

    meetsThreshold?: boolean;

    isTextAiGenerated = false;

    text?: string;

    evaluationResponse: any;

    isHealthcareIndustry = false;

    hasConfirmedAiUsage = false;

    user: any;

    constructor(props: any) {
        super(props);
        const { toolBarId, onCustomButtonClick } = this.props;

        this.state = {
            searchStartIndex: null,
            searchString: '',
            templateUrl: {},
            showError: false,
            showAiDisclaimer: false,
            emptyResponse: true,
        };

        this.responseModules = {
            toolbar: {
                container: `#${toolBarId}`,
                handlers: {
                    template: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.Template.id);
                    },
                    variable: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.Variable.id);
                    },
                    mention: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.Mention.id);
                    },
                    emoji: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.Emoji.id);
                    },
                    hashtag: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.HashTags.id);
                    },
                    videoRecorder: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.VideoRecorder.id);
                    },
                    audioRecorder: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.AudioRecorder.id);
                    },
                    responseGenerator: (): void => {
                        onCustomButtonClick(defaultSpecialButtonsOptions.ResponseGenerator.id);
                    },
                },
            },
            keyboard: {
                bindings: {
                    up: {
                        key: KeyCode.ArrowUp,
                        handler: (): any => {
                            return this.handleKeyPress(KeyCode.ArrowUp);
                        },
                    },
                    down: {
                        key: KeyCode.ArrowDown,
                        handler: (): any => {
                            return this.handleKeyPress(KeyCode.ArrowDown);
                        },
                    },
                    left: {
                        key: KeyCode.ArrowLeft,
                        handler: (): any => {
                            return this.handleKeyPress(KeyCode.ArrowLeft);
                        },
                    },
                    right: {
                        key: KeyCode.ArrowRight,
                        handler: (): any => {
                            return this.handleKeyPress(KeyCode.ArrowRight);
                        },
                    },
                    enter: {
                        key: KeyCode.Enter,
                        handler: (range: any, context: any): void => {
                            this.handleEnterKeyPress(range, context);
                        },
                    },
                    // override default quill binding
                    'header enter': {
                        key: KeyCode.Enter,
                        collapsed: true,
                        format: ['header'],
                        suffix: /^$/,
                        handler: (range: any, context: any): void => {
                            this.handleEnterKeyPress(range, context);
                        },
                    },
                    esc: {
                        key: KeyCode.Esc,
                        handler: (): void => {
                            this.handleKeyPress(KeyCode.Esc);
                        },
                    },
                    space: {
                        key: KeyCode.Space,
                        format: {
                            link: false,
                        },
                        handler: (range: any, context: any): boolean => {
                            this.handleLinkCheck(range, context);
                            return true;
                        },
                    },
                },
            },
            clipboard: {
                matchers: [
                    [
                        Node.TEXT_NODE,
                        (node: any, delta: any): any => {
                            if (node && node.data) {
                                const allMatches = node.data.match(urlRegex({ strict: false }));

                                if (allMatches && allMatches.length > 0) {
                                    const newDelta: any = {};
                                    const newOps = [];
                                    let allString = node.data;

                                    allMatches.forEach((matchItem: any): void => {
                                        const textParts = allString.split(matchItem);
                                        const beforeLink = textParts.shift();
                                        const newMatchItem = matchItem;

                                        newOps.push({
                                            insert: beforeLink,
                                            attributes: {
                                                background: false,
                                            },
                                        });
                                        newOps.push({
                                            insert: matchItem,
                                            attributes: { link: newMatchItem, background: false },
                                        });
                                        allString = textParts.join(newMatchItem);
                                    });
                                    newOps.push({
                                        insert: allString,
                                        attributes: {
                                            background: false,
                                        },
                                    });
                                    newDelta.ops = newOps;
                                    return newDelta;
                                }
                            }
                            forEach(delta.ops, (opsItem): void => {
                                if (!opsItem.attributes) {
                                    opsItem.attributes = {}; // eslint-disable-line no-param-reassign
                                }
                                opsItem.attributes.background = false; // eslint-disable-line no-param-reassign
                            });
                            return delta;
                        },
                    ],
                ],
            },
        };
        this.quillRef = null; // Quill instance
        this.reactQuillRef = null; // ReactQuill component
        this.unprivilegedEditor = null; // React Quill Editor
    }

    setLoading = (loading: boolean): void => {
        if (this.props.setLoading) {
            this.props.setLoading(loading);
        }
    };

    loadPreferencesAndIndustry = async (): Promise<void> => {
        if (this.props.vobReviewResponseEnabled && this.props.agencyId && this.props.tenantId && this.props.userId) {
            this.setLoading(true);
            await this.setHasConfirmedAiUsage();
            if (!this.hasConfirmedAiUsage) {
                const tenant: Record<string, any> = await getTenant(this.props.agencyId, this.props.tenantId);

                this.isHealthcareIndustry = tenant.industry.categoryID === 'health-care';
            }

            if (!this.props.generateAIResponseOnLoad) {
                this.setLoading(false);
            }
        }
    };

    setHasConfirmedAiUsage = async (): Promise<void> => {
        this.user = await getUser(this.props.agencyId!, this.props.tenantId!, this.props.userId!);
        this.hasConfirmedAiUsage = this.user.preferences?.hasConfirmedAiUsage === 'true';
    };

    componentDidMount = (): void => {
        this.isComponentMounted = true;
        this.loadPreferencesAndIndustry();
    };

    componentWillUnmount = (): void => {
        this.isComponentMounted = false;
    };

    componentDidUpdate = (prevProps: Props): void => {
        // re-check character count when selecting/deselecting Twitter source
        if (prevProps.twitterSelected !== this.props.twitterSelected) {
            const { totalCount } = this.getTextInfo();

            this.props.onCharCount(totalCount);
        }
    };

    handlePasteEvent = (): void => {
        if (this.props.onPaste) {
            this.props.onPaste();
        }
    };

    getFormats = (): string[] => {
        const formats: string[] = [];
        const { richTextOptions, enableTemplate, enableVariable, enableMention, enableEmoji, enableHashTag } =
            this.props;

        if (enableTemplate) {
            formats.push('template-url');
            formats.push('background');
        }
        if (enableVariable) formats.push('variable');
        if (enableMention) formats.push('mention');
        if (enableEmoji) formats.push('emoji');
        if (enableHashTag) formats.push('hashtag');

        if (richTextOptions.enableRichText) {
            const {
                enableBold,
                enableItalic,
                enableUnderline,
                enableStrikeThrough,
                enableBlockQuote,
                enableCodeBlock,
                enableOrderedList,
                enableBulletList,
                enableLink,
            } = richTextOptions.options!;

            if (enableBold) formats.push('bold');
            if (enableItalic) formats.push('italic');
            if (enableUnderline) formats.push('underline');
            if (enableStrikeThrough) formats.push('strike');
            if (enableBlockQuote) formats.push('blockquote');
            if (enableCodeBlock) formats.push('code-block');
            if (enableOrderedList || enableBulletList) formats.push('list');
            if (enableLink) formats.push('link');
        }
        return formats;
    };

    handleEnterKeyPress = (range: any, context: any): void => {
        const { searchStartIndex } = this.state;
        const { enableRespondOnEnterKey } = this.props;

        if (!isNull(searchStartIndex)) {
            this.handleKeyPress(KeyCode.Enter);
        } else if (enableRespondOnEnterKey) {
            this.respond();
        } else {
            const selection = this.quillRef.getSelection(true);

            if (selection.index > 0) {
                this.quillRef.insertText(selection.index, '\n');
                this.handleLinkCheck(range, context);
            }
        }
    };

    handleLinkCheck = (range: any, context: any): void => {
        if (context && context.prefix) {
            const allMatches = context.prefix.match(urlRegex());

            if (allMatches && allMatches.length) {
                const prefixLength = allMatches[0].length;
                const prefixStart = range.index - prefixLength;
                const url = this.quillRef.getText(prefixStart, prefixLength);

                this.quillRef.formatText(prefixStart, prefixLength, { link: url });
            }
        }
    };

    attachQuillRefs = (): void => {
        if (this.reactQuillRef && !this.quillRef && !this.unprivilegedEditor) {
            if (typeof this.reactQuillRef.getEditor !== 'function') return;
            this.quillRef = this.reactQuillRef.getEditor();
            this.unprivilegedEditor = this.reactQuillRef.makeUnprivilegedEditor(this.quillRef);
            const { canEdit, focus } = this.props;

            if (canEdit && focus) {
                this.quillRef.focus();
                this.quillRef.setSelection(this.quillRef.getLength());
            } else {
                this.quillRef.blur();
            }
            this.quillRef.container.addEventListener('click', this.handleClick);

            if (this.props.generateAIResponseOnLoad) {
                this.generateAIResponse(
                    this.props.tenantId!,
                    this.props.reviewId!,
                    this.props.reviewCreatedAt!,
                    this.props.locationId!,
                    this.props.userId!,
                    this.props.userLocale!,
                );
            }

            if (this.props.defaultValue?.length) {
                this.setText(this.props.defaultValue[0]?.text || '');

                if (this.props.vobReviewResponseEnabled) {
                    this.analyzeResponse(
                        this.props.tenantId!,
                        this.props.reviewId!,
                        this.props.reviewCreatedAt!,
                        this.props.locationId!,
                        this.props.userId!,
                    );
                }
            }
        }
    };

    handleClick = (event: any): void => {
        if (event && event.target && this.isComponentMounted) {
            const targetClassName = event.target.className || event.target.parentNode.className;

            if (targetClassName !== 'ql-action') {
                const selection = this.quillRef.getSelection(true);

                if (selection) {
                    const { onCustomButtonClick, onMentionClick, onHashTagClick } = this.props;
                    const { index } = selection;

                    switch (targetClassName) {
                        case `ql-${MetaDataTag.TemplateUrl}`:
                            this.setState({
                                searchStartIndex: index,
                                templateUrl: get(
                                    event,
                                    'target.originalValue',
                                    get(event, 'target.parentNode.originalValue', {}),
                                ),
                            });
                            onCustomButtonClick(defaultSpecialButtonsOptions.Template.id);
                            break;
                        case `ql-${MetaDataTag.Variable}`:
                            this.setState({
                                searchStartIndex: index,
                            });
                            onCustomButtonClick(defaultSpecialButtonsOptions.Variable.id);
                            break;
                        case `ql-${MetaDataTag.Mention}`:
                            if (onMentionClick) {
                                onMentionClick(event.target.originalValue || event.target.parentNode.originalValue);
                            } else {
                                this.setState({
                                    searchStartIndex: index,
                                });
                                onCustomButtonClick(defaultSpecialButtonsOptions.Mention.id);
                            }
                            break;
                        case `ql-${MetaDataTag.HashTag}`:
                            if (onHashTagClick) {
                                onHashTagClick(event.target.originalValue || event.target.parentNode.originalValue);
                            } else {
                                this.setState({
                                    searchStartIndex: index,
                                });
                                onCustomButtonClick(defaultSpecialButtonsOptions.HashTags.id);
                            }
                            break;
                        default:
                    }
                }
            }
        }
    };

    handleKeyPress = (keyCode: KeyCode): boolean => {
        const { onKeyNavigation } = this.props;
        const { searchStartIndex } = this.state;

        if (!isNull(searchStartIndex) && onKeyNavigation) {
            onKeyNavigation(keyCode);
            return false;
        }
        return true;
    };

    setText = (value: string): void => {
        this.quillRef?.setText(value);
    };

    setSearchStartIndex = (value: any): void => {
        if (this.isComponentMounted) {
            this.setState({
                searchStartIndex: value,
            });
        }
    };

    getSearchStartIndex = (): null | number => {
        const { searchStartIndex } = this.state;

        return searchStartIndex;
    };

    getSearchString = (): string => {
        const { searchString } = this.state;

        return searchString;
    };

    setSearchString = (value: any): void => {
        if (this.isComponentMounted) {
            this.setState({
                searchString: value,
            });
        }
    };

    getTemplateUrl = (): object => {
        const { templateUrl } = this.state;

        return templateUrl;
    };

    setTemplateUrl = (value: any): void => {
        if (this.isComponentMounted) {
            this.setState({
                templateUrl: value,
            });
        }
    };

    getSelection = (): number | null => {
        const { index } = this.quillRef.getSelection(true);

        return index;
    };

    setSelection = (value: any): void => {
        this.quillRef.setSelection(value);
    };

    setContents = (metaData: any): void => {
        this.reactQuillRef.setEditorContents(this.reactQuillRef.editor, metaData);
    };

    setFocus = (): void => {
        this.quillRef.focus();
    };

    setBlur = (): void => {
        this.quillRef.blur();
    };

    getBounds = (index: any): object => {
        return this.quillRef.getBounds(index >= 0 ? index : 0);
    };

    removeFormat = (index: number, length: number): void => {
        this.quillRef.removeFormat(index, length);
    };

    removeAllFormats = (): void => {
        const length = this.quillRef.getLength();

        this.removeFormat(0, length - 1);
    };

    handleWidgetTrigger = (currentChar: string): void => {
        const { searchStartIndex, searchString } = this.state;
        const { onWidgetTrigger, enableTemplate, enableVariable, enableMention, enableEmoji, enableHashTag } =
            this.props;
        const selection = this.quillRef.getSelection(true);

        if (selection && this.isComponentMounted) {
            const { index } = selection;

            if (index < searchStartIndex!) {
                onWidgetTrigger({
                    displayTemplate: false,
                    displayVariable: false,
                    displayMention: false,
                    displayEmoji: false,
                    displayHashTag: false,
                });
                this.setState({
                    searchStartIndex: null,
                    searchString: '',
                    templateUrl: {},
                });
            } else {
                let text = currentChar;

                if (!isNull(searchStartIndex)) {
                    text = this.quillRef.getText(searchStartIndex - 1, searchString.length);
                }
                let widgetName = '';

                if (!isNull(text)) {
                    switch (text[0]) {
                        case defaultSpecialButtonsOptions.Template.SearchKey:
                            if (enableTemplate) {
                                widgetName = defaultSpecialButtonsOptions.Template.id;
                            }
                            break;
                        case defaultSpecialButtonsOptions.Variable.SearchKey:
                            if (enableVariable) {
                                widgetName = defaultSpecialButtonsOptions.Variable.id;
                            }
                            break;
                        case defaultSpecialButtonsOptions.Mention.SearchKey:
                            if (enableMention) {
                                widgetName = defaultSpecialButtonsOptions.Mention.id;
                            }
                            break;
                        case defaultSpecialButtonsOptions.HashTags.SearchKey:
                            if (enableHashTag) {
                                widgetName = defaultSpecialButtonsOptions.HashTags.id;
                            }
                            break;
                        case defaultSpecialButtonsOptions.Emoji.SearchKey:
                            if (enableEmoji) {
                                widgetName = defaultSpecialButtonsOptions.Emoji.id;
                                if (currentChar === ' ') {
                                    const shortText = trim(text).substring(1);
                                    const shortEmoji = EmojisShorthand[shortText];

                                    if (shortEmoji && shortEmoji.unicode) {
                                        this.handleCustomWidgetValueSelect(
                                            { label: getUnicodeString(shortEmoji.unicode) },
                                            defaultSpecialButtonsOptions.Emoji.id,
                                        );
                                    }
                                }
                            }
                            break;
                        default:
                            break;
                    }
                    let openWidget = true;

                    if (widgetName) {
                        if (isNull(searchStartIndex)) {
                            this.setState({
                                searchStartIndex: index,
                            });
                        }
                        if (!isNull(searchStartIndex)) {
                            if (text.indexOf(' ') >= 0 && defaultSpecialButtonsOptions[widgetName].closeOnSpace) {
                                openWidget = false;
                                this.setState({
                                    searchStartIndex: null,
                                    searchString: '',
                                    templateUrl: {},
                                });
                            } else if (
                                index - searchStartIndex! <=
                                defaultSpecialButtonsOptions[widgetName].searchStartIndex
                            ) {
                                openWidget = false;
                            }
                        } else if (defaultSpecialButtonsOptions[widgetName].searchStartIndex) {
                            openWidget = false;
                        }
                        onWidgetTrigger({
                            displayTemplate: widgetName === defaultSpecialButtonsOptions.Template.id && openWidget,
                            displayVariable: widgetName === defaultSpecialButtonsOptions.Variable.id && openWidget,
                            displayMention: widgetName === defaultSpecialButtonsOptions.Mention.id && openWidget,
                            displayEmoji: widgetName === defaultSpecialButtonsOptions.Emoji.id && openWidget,
                            displayHashTag: widgetName === defaultSpecialButtonsOptions.HashTags.id && openWidget,
                        });
                    }
                }
            }
        }
    };

    handleTextChange = (content: any, delta: any, source: any, editor: any): void => {
        setTimeout(() => {
            const { text, totalCount } = this.getTextInfo();
            const { searchStartIndex, searchString } = this.state;
            const { onCharCount, onTextChange, onManualTyping } = this.props;
            const latestInsert = get(delta, `ops[${delta.ops.length - 1}].insert`, null);

            this.setState({
                emptyResponse: totalCount === 0,
            });

            if (this.props.vobReviewResponseEnabled) {
                const prevText = this.textHistory.find((entry) => entry.text === text);

                this.isTextAnalyzed = !!prevText;
                this.meetsThreshold = prevText?.meetsThreshold;
                this.isTextAiGenerated = !!prevText?.aiGenerated;

                if (this.props.onIsTextAnalyzedChanged) {
                    this.props.onIsTextAnalyzedChanged(this.isTextAnalyzed);
                }

                if (this.props.onMeetsThresholdChanged) {
                    this.props.onMeetsThresholdChanged(this.meetsThreshold);
                }
            }

            onCharCount(totalCount);
            if (!isNull(searchStartIndex) && this.isComponentMounted) {
                forEach(delta.ops, (deltaItem: any) => {
                    if (deltaItem.insert) {
                        this.setState({
                            searchString: searchString + deltaItem.insert,
                        });
                    } else if (deltaItem.delete) {
                        this.setState({
                            searchString: searchString.slice(0, -deltaItem.delete),
                        });
                    }
                });
            }
            if (onTextChange) {
                onTextChange({
                    text,
                    html: this.unprivilegedEditor.getHTML(),
                    metadata: convertQuillToMetadata(editor.getContents()),
                });
            }
            if (source !== 'api' && this.isComponentMounted) {
                this.handleWidgetTrigger(latestInsert);
            }
            if (source === 'user' && onManualTyping) {
                onManualTyping();
            }

            this.text = text;
        }, 0);
    };

    respond = async (): Promise<void> => {
        if (this.props.vobReviewResponseEnabled && !this.hasConfirmedAiUsage && this.isTextAiGenerated) {
            this.setLoading(true);
            await this.setHasConfirmedAiUsage();
            this.setLoading(false);
            this.setState({ showAiDisclaimer: !this.hasConfirmedAiUsage });
            if (!this.hasConfirmedAiUsage) return;
        }

        const { onRespond } = this.props;

        if (onRespond) {
            const { text, contents } = this.getTextInfo();

            onRespond({
                text,
                html: this.unprivilegedEditor.getHTML(),
                metadata: convertQuillToMetadata(contents),
                showWarning: this.props.vobReviewResponseEnabled && (!this.isTextAnalyzed || !this.meetsThreshold),
                evaluationResponse: this.evaluationResponse,
            });
        }
    };

    generateAIResponse = async (
        tenantId: number,
        reviewId: string,
        reviewCreatedAt: string,
        locationId: number,
        userId: number,
        userLocale: LocaleCode,
        onSuccess?: (vobData: any) => void,
    ): Promise<void> => {
        this.setLoading(true);
        const { text } = this.getTextInfo();

        try {
            const { data } = await axios.post(`/api/tenants/${tenantId}/nuvi/vob/enhance`, {
                args: {
                    tenantId: tenantId.toString(),
                    reviewId,
                    createdAt: reviewCreatedAt,
                    locationId,
                    userId: userId.toString(),
                    userLocale: userLocale.toString(),
                    response: text,
                },
            });

            this.evaluationResponse = data.data;
            this.isTextAiGenerated = true;

            if (!this.textHistory.some(({ text }) => text === data.data.response)) {
                this.textHistory = [
                    ...this.textHistory,
                    {
                        aiGenerated: true,
                        meetsThreshold: data.data.meets_threshold,
                        text: data.data.response,
                    },
                ];
            }

            if (this.quillRef) {
                this.quillRef.setText('');

                this.quillRef.insertText(0, data.data.response, 'user');

                // Move cursor to the end of the new text
                this.quillRef.setSelection(this.quillRef.getLength(), 0);
            }

            if (this.props.onMeetsThresholdChanged) {
                this.props.onMeetsThresholdChanged(data.data.meets_threshold);
            }

            if (onSuccess) {
                onSuccess(data.data);
            }

            this.setLoading(false);
            this.setState({ showError: false });
        } catch {
            this.errorCount += 1;
            this.setLoading(false);
            this.setState({ showError: true });
        }
    };

    analyzeResponse = async (
        tenantId: number,
        reviewId: string,
        reviewCreatedAt: string,
        locationId: number,
        userId: number,
        onSuccess?: (score: number, vobData: any) => void,
    ): Promise<void> => {
        this.setLoading(true);
        const { text } = this.getTextInfo();

        try {
            const { data } = await axios.post(`/api/tenants/${tenantId}/nuvi/vob/evaluate`, {
                args: {
                    tenantId: tenantId.toString(),
                    reviewId,
                    createdAt: reviewCreatedAt,
                    locationId,
                    userId: userId.toString(),
                    response: text,
                },
            });

            this.isTextAnalyzed = true;
            if (this.props.onIsTextAnalyzedChanged) {
                this.props.onIsTextAnalyzedChanged(this.isTextAnalyzed);
            }

            if (!this.textHistory.some(({ text: textH }) => textH === text)) {
                this.textHistory = [
                    ...this.textHistory,
                    {
                        aiGenerated: this.isTextAiGenerated,
                        meetsThreshold: data.data.meets_threshold,
                        text: data.data.response,
                    },
                ];
            }

            this.meetsThreshold = data.data.meets_threshold;

            if (this.props.onMeetsThresholdChanged) {
                this.props.onMeetsThresholdChanged(data.data.meets_threshold);
            }

            if (onSuccess) {
                onSuccess(data.data.publish_score, data.data);
            }

            this.setLoading(false);
            this.setState({ showError: false });
        } catch {
            this.errorCount += 1;
            this.setLoading(false);
            this.setState({ showError: true });
        }
    };

    getTextInfo = (): { text: string; totalCount: number; contents: object } => {
        let text = '';
        let textCount = -1;
        let emojiCount = 0;
        const contents = this.quillRef.getContents();

        if (contents && contents.ops) {
            contents.ops.forEach((opsItem: any) => {
                if (opsItem && opsItem.insert) {
                    if (isString(opsItem.insert)) {
                        if (this.props.twitterSelected && opsItem.attributes && opsItem.attributes.link) {
                            textCount += 23;
                        } else {
                            textCount += opsItem.insert.length;
                        }

                        text += opsItem.insert;
                    } else if (opsItem.insert[MetaDataTag.Emoji]) {
                        const emojiCode = get(opsItem, `insert.${MetaDataTag.Emoji}`, null);

                        if (emojiCode) {
                            text += emojiCode;
                            emojiCount += 1;
                        }
                    } else if (opsItem.insert[MetaDataTag.Variable]) {
                        const variableValue = get(opsItem, `insert.${MetaDataTag.Variable}`, '');

                        text += variableValue;
                        textCount += variableValue.length - 2;
                    } else if (opsItem.insert[MetaDataTag.Mention]) {
                        const mentionValue = get(opsItem, `insert.${MetaDataTag.Mention}`, '');

                        text += mentionValue;
                        textCount += mentionValue.length - 2;
                    } else if (opsItem.insert[MetaDataTag.TemplateUrl]) {
                        const templateValue = get(opsItem, `insert.${MetaDataTag.TemplateUrl}`, '');

                        text += templateValue;
                        textCount += templateValue.length - 2;
                    } else if (opsItem.insert[MetaDataTag.HashTag]) {
                        const hashTagValue = get(opsItem, `insert.${MetaDataTag.HashTag}`, '');

                        text += hashTagValue;
                        textCount += hashTagValue.length - 2;
                    }
                }
            });
        }
        return {
            text: trim(text),
            totalCount: textCount + emojiCount,
            contents,
        };
    };

    handleCustomWidgetValueSelect = (value: any, widgetName: string): void => {
        const { searchStartIndex, searchString } = this.state;
        const { includeMentionPrefix, includeHashTagPrefix } = this.props;
        const inputText = value.description || value.label;
        const clonedValue = clone(value);

        if (inputText && this.isComponentMounted) {
            setTimeout(() => {
                const { index } = this.quillRef.getSelection(true);

                switch (widgetName) {
                    case defaultSpecialButtonsOptions.Template.id:
                        if (value && value.isUrl) {
                            clonedValue.text = `{{${value.label}}}`;
                            this.quillRef.insertEmbed(index, MetaDataTag.TemplateUrl, clonedValue);
                            this.quillRef.setSelection(index + 1);
                            this.setState({
                                templateUrl: {},
                            });
                        } else {
                            this.quillRef.insertText(index, inputText);
                            this.quillRef.formatText(index, inputText.length, {
                                background: Color.Turquoise8,
                            });
                            this.quillRef.insertText(index + inputText.length, ' ');
                            this.quillRef.formatText(index + inputText.length, 1, 'background', false);
                            setTimeout(() => {
                                this.quillRef.formatText(
                                    index ? index - 1 : index,
                                    inputText.length + 1,
                                    'background',
                                    false,
                                );
                            }, 1000);
                        }
                        break;

                    case defaultSpecialButtonsOptions.Variable.id:
                        this.quillRef.insertEmbed(index, MetaDataTag.Variable, value);
                        this.quillRef.setSelection(index + 1);
                        break;

                    case defaultSpecialButtonsOptions.Mention.id:
                        clonedValue.label = `${includeMentionPrefix ? '@' : ''}${value.label}`;
                        this.quillRef.insertEmbed(index, MetaDataTag.Mention, clonedValue);
                        this.quillRef.setSelection(index + 1);
                        break;

                    case defaultSpecialButtonsOptions.Emoji.id:
                        this.quillRef.insertEmbed(index, MetaDataTag.Emoji, value);
                        this.quillRef.setSelection(index + 1);
                        break;

                    case defaultSpecialButtonsOptions.HashTags.id:
                        clonedValue.label = `${includeHashTagPrefix ? '#' : ''}${value.label}`;
                        this.quillRef.insertEmbed(index, MetaDataTag.HashTag, clonedValue);
                        this.quillRef.insertText(index + 1, ' ');
                        this.quillRef.setSelection(index + 2);
                        break;

                    case defaultSpecialButtonsOptions.SuggestedResponse.id:
                        this.quillRef.insertText(index, inputText);
                        this.quillRef.formatText(index, inputText.length, {
                            background: Color.Turquoise8,
                        });
                        this.quillRef.insertText(index + inputText.length, ' ');
                        this.quillRef.formatText(index + inputText.length, 1, 'background', false);
                        setTimeout(() => {
                            this.quillRef.formatText(
                                index ? index - 1 : index,
                                inputText.length + 1,
                                'background',
                                false,
                            );
                        }, 1000);
                        break;

                    default:
                        break;
                }
                if (!isNull(searchStartIndex)) {
                    this.quillRef.deleteText(searchStartIndex - 1, searchString.length + 1);
                }
                this.handleCustomWidgetClose();
            }, 0);
        }
    };

    handleCustomWidgetClose = (): void => {
        const { onWidgetTrigger } = this.props;

        onWidgetTrigger({
            displayTemplate: false,
            displayVariable: false,
            displayMention: false,
            displayEmoji: false,
            displayHashTag: false,
        });
        if (this.isComponentMounted) {
            this.setState({
                searchStartIndex: null,
                searchString: '',
                templateUrl: {},
            });
        }
    };

    handleCloseError = (): void => {
        this.setState({ showError: false });
    };

    handleDisclaimerModalClose = async (confirmedAiUsage: boolean): Promise<void> => {
        if (confirmedAiUsage) {
            this.hasConfirmedAiUsage = true;
            this.user.preferences.hasConfirmedAiUsage = 'true';
            await setUserPreference(this.props.agencyId!, this.props.tenantId!, this.user.id, {
                preferenceKey: PreferenceKey.HasConfirmedAIUsage,
                preferenceValue: 'true',
            });
            this.respond();
        }
        this.setState({ showAiDisclaimer: false });
    };

    render(): JSX.Element {
        const {
            onFocus,
            onBlur,
            placeholder,
            backgroundColor,
            vobReviewResponseEnabled,
            reviewId,
            locationId,
            userId,
            tenantId,
        } = this.props;

        return (
            <>
                {this.state.showAiDisclaimer && (
                    <DisclaimerModal
                        onDecision={this.handleDisclaimerModalClose}
                        isHealthcare={this.isHealthcareIndustry}
                    />
                )}
                <div
                    className={classNames('editor-bounds-container', {
                        'vob-scoring': vobReviewResponseEnabled,
                    })}
                >
                    {vobReviewResponseEnabled && (
                        <VoBScoring meetsThreshold={this.meetsThreshold} emptyResponse={this.state.emptyResponse} />
                    )}
                    {this.props.loading && <OverlayDisplay />}
                    {this.state.showError && <ErrorMessage errorCount={0} onClose={this.handleCloseError} />}
                    {vobReviewResponseEnabled && (
                        <TrackedReactQuill
                            forwardedRef={(el: any): void => {
                                if (el) {
                                    this.reactQuillRef = el;
                                    this.attachQuillRefs();
                                }
                            }}
                            onChange={this.handleTextChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            modules={this.responseModules}
                            formats={this.getFormats()}
                            className="r4e-response-editor-content"
                            theme="snow"
                            bounds=".editor-bounds-container"
                            style={{
                                backgroundColor,
                            }}
                            eventsToTrack={[
                                {
                                    event: PendoEvents.COPY,
                                    metadata: {
                                        elementId: 'vob-review-response-editor',
                                        reviewId,
                                        tenantId,
                                        locationId,
                                        userId,
                                        text: this.text ?? '',
                                    },
                                },
                                {
                                    event: PendoEvents.PASTE,
                                    metadata: {
                                        elementId: 'vob-review-response-editor',
                                        reviewId,
                                        tenantId,
                                        locationId,
                                        userId,
                                        text: this.text ?? '',
                                    },
                                    eventHandler: this.handlePasteEvent,
                                },
                            ]}
                        />
                    )}
                    {!vobReviewResponseEnabled && (
                        <ReactQuill
                            ref={(el: any): void => {
                                if (el) {
                                    this.reactQuillRef = el;
                                    this.attachQuillRefs();
                                }
                            }}
                            onChange={this.handleTextChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            modules={this.responseModules}
                            formats={this.getFormats()}
                            className="r4e-response-editor-content"
                            theme="snow"
                            bounds=".editor-bounds-container"
                            style={{
                                backgroundColor,
                            }}
                        />
                    )}
                </div>
            </>
        );
    }
}
